// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { HEADER_HEIGHT_CSS_VAR } from "@conklin/XDL/constants";
import { xScreen } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { injectCss } from "@insite/mobius/utilities";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import React, { FC } from "react";
import styled, { css } from "styled-components";

interface XngStickyProps {
    css?: StyledProp<XngStickyComponentProps>;
    stickyOffset?: string;
    stopBreakpoint?: "xs" | "sm" | "md" | "lg" | "xl";
    topValue?: number;
    [key: string]: any;
}

export type XngStickyComponentProps = MobiusStyledComponentProps<"div", XngStickyProps>;

export const XngStickyWrapper = styled.div<XngStickyComponentProps>`
    position: sticky;
    z-index: 1;
    ${({ stopBreakpoint }) => css`
        ${xScreen("lt", stopBreakpoint || "sm")} {
            position: static;
        }
    `};
    @media print {
        position: static;
    }
    top: ${({ stickyOffset }: any) => stickyOffset || "unset"};

    ${injectCss}
`;

const XngSticky: FC<XngStickyProps> = ({ className = "xng-sticky", stopBreakpoint = "sm", ...props }) => {
    if (typeof document === "undefined") {
        return <StyledWrapper {...props} />;
    }
    const cssRoot = document?.querySelector(":root") as HTMLElement;
    const stickyHeader = cssRoot.style.getPropertyValue(HEADER_HEIGHT_CSS_VAR).replace("px", "") || "168";
    const topValue = props?.topValue;
    const [offset, setOffset] = React.useState(`${parseFloat(stickyHeader) + (topValue ?? 10)}px`);
    const updateOffset = () => {
        const updatedStickyHeader = cssRoot.style.getPropertyValue(HEADER_HEIGHT_CSS_VAR).replace("px", "") || "168";
        setOffset(`${parseFloat(updatedStickyHeader) + (topValue ?? 10)}px`);
    };
    React.useEffect(() => {
        // Initial update
        updateOffset();
        // Observe changes to the style of cssRoot
        const observer = new MutationObserver(updateOffset);
        observer.observe(cssRoot, { attributes: true, attributeFilter: ["style"] });
        return () => observer.disconnect();
    }, []);
    return <XngStickyWrapper {...props} stickyOffset={offset} />;
};

export default XngSticky;
